import { Edit3, Eye } from "lucide-react";
import PropTypes from "prop-types";
import React from "react";

/**
 * Split-pane layout for visual editors.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.editor - The form/controls content.
 * @param {React.ReactNode} props.preview - The visual preview content.
 * @param {string} [props.previewTitle] - Title for the preview pane.
 * @param {string} [props.layout='split'] - 'split' (Editor Left/Flex, Preview Right/Fixed) or 'builder' (Editor Left/Fixed, Preview Right/Flex).
 */
export default function VisualEditorLayout({
	editor,
	preview,
	previewTitle,
	layout = "split",
}) {
	if (layout === "builder") {
		return (
			<div className="flex gap-0 min-h-[calc(100vh-220px)] overflow-hidden">
				{/* Sidebar (Editor Controls) */}
				<div className="w-[360px] flex-shrink-0 bg-slate-900/90 border-r border-white/5 flex flex-col">
					<div className="p-4 border-b border-white/5 flex items-center justify-between">
						<span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
							<Edit3 className="w-3.5 h-3.5 inline mr-2" />
							Editor
						</span>
					</div>
					<div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">
						{editor}
					</div>
				</div>

				{/* Main Canvas (Preview) */}
				<div className="flex-1 bg-black/50 overflow-y-auto custom-scrollbar relative">
					<div className="min-h-full p-8 flex justify-center">
						<div className="w-full max-w-[1200px] bg-[#0B0F19] shadow-2xl min-h-[800px] animate-in fade-in duration-500">
							{preview}
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Default 'split' layout (Original)
	return (
		<div className="flex gap-6 min-h-[600px]">
			{/* Editor pane (Main, Left) */}
			<div className="flex-1 space-y-4 overflow-y-auto max-h-[80vh] pr-2 custom-scrollbar">
				{editor}
			</div>

			{/* Preview pane (Sidebar, Right) */}
			<div className="w-[420px] flex-shrink-0 sticky top-0 self-start">
				<div className="bg-slate-900/50 border border-white/5 rounded-xl overflow-hidden">
					<div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
						<Eye className="w-3.5 h-3.5 text-slate-500" />
						<span className="text-xs text-slate-400 font-medium">
							{previewTitle || "Preview"}
						</span>
					</div>
					<div className="max-h-[75vh] overflow-y-auto custom-scrollbar">
						{preview}
					</div>
				</div>
			</div>
		</div>
	);
}

VisualEditorLayout.propTypes = {
	editor: PropTypes.node.isRequired,
	preview: PropTypes.node.isRequired,
	previewTitle: PropTypes.string,
	layout: PropTypes.oneOf(["split", "builder"]),
};
